// src/components/demo/DemoFeatureTourSection.jsx
import React from "react";
import {
  FaNewspaper,
  FaCalendarAlt,
  FaUsers,
  FaFileAlt,
  FaStore,
  FaLandmark,
  FaProjectDiagram,
  FaSitemap,
} from "react-icons/fa";

const features = [
  {
    icon: FaNewspaper,
    title: "Actualités & page d’accueil",
    tag: "Communication",
    desc: "Publiez des actualités illustrées, mettez en avant les informations prioritaires et animez la page d’accueil de la commune en quelques clics.",
    link: "/actualites",
  },
  {
    icon: FaCalendarAlt,
    title: "Agenda & événements",
    tag: "Calendrier",
    desc: "Centralisez toutes les dates importantes : cérémonies, réunions publiques, événements associatifs, manifestations sportives, vie culturelle…",
    link: "/agenda",
  },
  {
    icon: FaUsers,
    title: "Associations de la commune",
    tag: "Vie locale",
    desc: "Répertoire des associations avec fiche détaillée (contacts, activités, horaires, lieux de pratique) pour valoriser le tissu associatif local.",
    // link à ajouter quand tu auras la page publique associations
    link: undefined,
  },
  {
    icon: FaStore,
    title: "Commerces & artisans",
    tag: "Vie économique",
    desc: "Annuaire des commerces et services de proximité : description, coordonnées, liens vers leurs sites ou réseaux sociaux.",
    // link à ajouter quand tu auras la page publique commerces
    link: undefined,
  },
  {
    icon: FaLandmark,
    title: "Élus & services municipaux",
    tag: "Mairie",
    desc: "Présentation du maire, des adjoints, des conseillers, ainsi que l’organisation des services municipaux avec horaires d’accueil et contacts.",
    link: "/equipe-municipale",
  },
  {
    icon: FaFileAlt,
    title: "Publications & documents",
    tag: "Transparence",
    desc: "Mettez en ligne les comptes rendus de conseil, bulletins municipaux, règlements, arrêtés et autres documents téléchargeables.",
    link: "/publications",
  },
  {
    icon: FaProjectDiagram,
    title: "Projets de la commune",
    tag: "Projets",
    desc: "Suivez les grands projets (travaux, aménagements, consultations citoyennes…) et informez les habitants de l’avancement.",
    link: "/projets",
  },
  {
    icon: FaSitemap,
    title: "Pages, menus & messagerie interne",
    tag: "Administration",
    desc: "Créez vos propres pages via un éditeur de contenu, organisez l’arborescence des menus et utilisez la messagerie interne entre élus et services pour suivre les demandes et contenus.",
    // partie plutôt back-office, pas forcément de lien public
    link: undefined,
  },
];

export default function DemoFeatureTourSection() {
  return (
    <section
      id="fonctionnalites"
      className="mx-auto max-w-[1200px] px-4 sm:px-6 mt-16"
    >
      <Header
        title="Découvrir les principales fonctionnalités"
        subtitle="MonAppliMairie centralise la communication, la vie locale et l’administration de votre site communal"
      />

      <p className="mt-4 text-center text-sm text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
        Le site public de la commune est entièrement géré depuis MonAppliMairie :
        actualités, agenda, associations, commerces, projets, documents, élus,
        services municipaux… et une partie back-office pour les menus, les pages
        et la messagerie interne entre élus et services.
      </p>

      <div className="mt-10 grid gap-6 md:grid-cols-2">
        {features.map((f) => {
          const Icon = f.icon;
          return (
            <article
              key={f.title}
              className="group relative overflow-hidden rounded-2xl border border-slate-200/70 dark:border-slate-700/80 bg-white/90 dark:bg-slate-900/85 shadow-sm p-5 md:p-6 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl"
            >
              {/* Fond dégradé subtil */}
              <div className="pointer-events-none absolute inset-0 opacity-60 mix-blend-soft-light">
                <div className="absolute -top-10 -right-10 w-28 h-28 bg-gradient-to-br from-sky-500/25 via-primary/25 to-purple-500/25 blur-2xl" />
                <div className="absolute -bottom-10 -left-10 w-28 h-28 bg-gradient-to-br from-emerald-500/25 via-amber-400/25 to-pink-500/25 blur-2xl" />
              </div>

              <div className="relative flex gap-4">
                <div className="mt-1">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500/15 via-primary/20 to-purple-500/25 text-primary group-hover:scale-110 group-hover:rotate-1 transition-transform duration-300">
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {f.tag && (
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                        {f.tag}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                    {f.desc}
                  </p>
                  {/* {f.link && (
                    <a
                      href={f.link}
                      className="mt-3 inline-flex items-center text-xs md:text-sm font-semibold text-primary hover:underline uppercase tracking-wide"
                    >
                      Voir un exemple&nbsp;→
                    </a>
                  )} */}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function Header({ title, subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 text-center">
      <div className="inline-flex items-center gap-2 rounded-full bg-primary/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
        <span>Fonctionnalités principales</span>
      </div>
      <div>
        <h2 className="text-3xl md:text-[34px] font-semibold tracking-wide">
          {title}
        </h2>
        <p className="text-sm text-muted mt-1 max-w-2xl">
          {subtitle}
        </p>
      </div>
    </div>
  );
}
